// services/PromotionService.js
import { collection, getDocs, doc } from 'firebase/firestore';
import { db } from '../firebase/config';

class PromotionService {
  constructor() {
    this.cache = {
      data: null,
      timestamp: null,
      duration: 5 * 60 * 1000 // 5 minutes in milliseconds
    };
  }

  /**
   * Check if cache is still valid
   */
  isCacheValid() {
    if (!this.cache.data || !this.cache.timestamp) return false;
    const now = Date.now();
    return (now - this.cache.timestamp) < this.cache.duration;
  }

  /**
   * Get cached promotions data
   */
  getCachedPromotions() {
    if (this.isCacheValid()) {
      return this.cache.data;
    }
    return null;
  }

  /**
   * Set cached promotions data
   */
  setCachedPromotions(data) {
    this.cache.data = data;
    this.cache.timestamp = Date.now();
  }

  /**
   * Clear cache
   */
  clearCache() {
    this.cache.data = null;
    this.cache.timestamp = null;
  }

  /**
   * Sort categories with "welcome bonus" first and custom ordering
   */
  sortCategories(categories) {
    let sortedCategories = [...categories];

    // First, normalize and combine categories
    const processedCategories = [];
    let hasLosingOrRescue = false;
    const rebateVariations = new Set();

    // Process each category
    sortedCategories.forEach(cat => {
      const lowerCat = cat.toLowerCase();

      // Handle losing/rescue combination
      if (lowerCat === 'losing' || lowerCat === 'rescue') {
        hasLosingOrRescue = true;
        return; // Don't add these individually
      }

      // Handle rebate variations
      if (lowerCat.includes('rebate')) {
        rebateVariations.add(cat);
        return; // Don't add these individually yet
      }

      // Add other categories as-is
      processedCategories.push(cat);
    });

    // Add combined "losing rescue" if either existed (only once)
    if (hasLosingOrRescue) {
      processedCategories.push('Losing Rescue');
    }

    // Add single "rebate" if any rebate variations existed
    if (rebateVariations.size > 0) {
      processedCategories.push('Rebate');
    }

    // Now sort with special ordering
    const finalCategories = [];
    const specialOrder = ['All', 'Welcome Bonus'];
    const endOrder = ['Referral', 'Others'];

    // Add special order categories first
    specialOrder.forEach(special => {
      const found = processedCategories.find(cat => cat.toLowerCase() === special.toLowerCase());
      if (found) {
        finalCategories.push(found);
      }
    });

    // Add middle categories (everything except special and end categories)
    processedCategories.forEach(cat => {
      const lowerCat = cat.toLowerCase();
      const isSpecial = specialOrder.some(s => s.toLowerCase() === lowerCat);
      const isEnd = endOrder.some(e => e.toLowerCase() === lowerCat);

      if (!isSpecial && !isEnd) {
        finalCategories.push(cat);
      }
    });

    // Add end order categories last
    endOrder.forEach(end => {
      const found = processedCategories.find(cat => cat.toLowerCase() === end.toLowerCase());
      if (found) {
        finalCategories.push(found);
      }
    });

    return finalCategories;
  }

  /**
   * Fetch initial promotions with aggressive optimization
   */
  async fetchInitialPromotions(limit = 30, forceRefresh = false) {
    try {
      // Check cache first (unless forcing refresh)
      if (!forceRefresh) {
        const cachedData = this.getCachedPromotions();
        if (cachedData) {
          console.log('Using cached promotions data');
          return {
            success: true,
            data: {
              promotions: cachedData.promotions.slice(0, limit),
              categories: cachedData.categories,
              hasMore: cachedData.promotions.length > limit,
              total: cachedData.promotions.length
            },
            fromCache: true
          };
        }
      }

      console.log('Fetching initial promotions from Firebase');
      
      // Fetch only first few brands for initial load
      const brandsSnapshot = await getDocs(collection(db, "brands"));
      
      if (brandsSnapshot.empty) {
        const emptyData = { promotions: [], categories: ['All'], hasMore: false, total: 0 };
        return {
          success: true,
          data: emptyData,
          fromCache: false
        };
      }

      // Process all brands to load all promotions
      const initialBrands = brandsSnapshot.docs;
      let allPromotions = [];
      let categorySet = new Set(['All']);
      
      // Fetch promotions for initial brands in parallel
      const promotionPromises = initialBrands.map(async (brandDoc) => {
        const brand = { id: brandDoc.id, ...brandDoc.data() };
        
        try {
          // Fetch promotions from the subcollection
          const promotionsRef = collection(doc(db, "brands", brandDoc.id), "promotions");
          const promotionsSnapshot = await getDocs(promotionsRef);
          
          const brandPromotions = [];
          
          if (!promotionsSnapshot.empty) {
            promotionsSnapshot.forEach(promoDoc => {
              const promoData = promoDoc.data();
              
              // Add brand info to each promotion
              const enrichedPromotion = {
                ...promoData,
                id: promoDoc.id,
                brandId: brand.id,
                brandName: brand.name,
                brandLogo: brand.logoImage,
                brandSlug: brand.slug,
                brandWebsite: brand.websiteUrl,
                brandRating: brand.rating,
                brandCreatedAt: brand.createdAt
              };
              
              brandPromotions.push(enrichedPromotion);
              
              // Collect unique categories
              if (promoData.category) {
                categorySet.add(promoData.category);
              }
            });
          }
          
          return brandPromotions;
        } catch (error) {
          console.error(`Error fetching promotions for brand ${brand.id}:`, error);
          return [];
        }
      });
      
      // Wait for initial brand promotion fetches to complete
      const brandPromotionArrays = await Promise.all(promotionPromises);
      
      // Flatten the arrays
      allPromotions = brandPromotionArrays.flat();
      
      // Sort promotions by date (newest first)
      allPromotions.sort((a, b) => {
        if (a.createdAt && b.createdAt) {
          return b.createdAt.toDate() - a.createdAt.toDate();
        }
        return 0;
      });
      
      const categoriesArray = this.sortCategories(Array.from(categorySet));
      
      // Take only the requested limit
      const limitedPromotions = allPromotions.slice(0, limit);
      
      const result = {
        promotions: limitedPromotions,
        categories: categoriesArray,
        hasMore: allPromotions.length > limit,
        total: allPromotions.length
      };
      
      // Cache the full data for later use
      this.setCachedPromotions({
        promotions: allPromotions,
        categories: categoriesArray
      });
      
      // All promotions are already loaded, no background fetch needed
      
      return {
        success: true,
        data: result,
        fromCache: false
      };
      
    } catch (error) {
      console.error("Error fetching initial promotions:", error);
      return {
        success: false,
        error: error.message || "Failed to load promotions",
        data: null
      };
    }
  }

  /**
   * Fetch remaining promotions in background
   */
  async fetchRemainingPromotionsInBackground(remainingBrands) {
    if (remainingBrands.length === 0) return;
    
    try {
      console.log('Fetching remaining promotions in background');
      
      let additionalPromotions = [];
      let categorySet = new Set();
      
      // Get current cached data
      const currentCache = this.getCachedPromotions();
      if (currentCache) {
        additionalPromotions = [...currentCache.promotions];
        currentCache.categories.forEach(cat => categorySet.add(cat));
      }
      
      // Fetch remaining brands in smaller batches
      const batchSize = 5;
      for (let i = 0; i < remainingBrands.length; i += batchSize) {
        const batch = remainingBrands.slice(i, i + batchSize);
        
        const batchPromises = batch.map(async (brandDoc) => {
          const brand = { id: brandDoc.id, ...brandDoc.data() };
          
          try {
            const promotionsRef = collection(doc(db, "brands", brandDoc.id), "promotions");
            const promotionsSnapshot = await getDocs(promotionsRef);
            
            const brandPromotions = [];
            
            if (!promotionsSnapshot.empty) {
              promotionsSnapshot.forEach(promoDoc => {
                const promoData = promoDoc.data();
                
                const enrichedPromotion = {
                  ...promoData,
                  id: promoDoc.id,
                  brandId: brand.id,
                  brandName: brand.name,
                  brandLogo: brand.logoImage,
                  brandSlug: brand.slug,
                  brandWebsite: brand.websiteUrl,
                  brandRating: brand.rating,
                  brandCreatedAt: brand.createdAt
                };
                
                brandPromotions.push(enrichedPromotion);
                
                if (promoData.category) {
                  categorySet.add(promoData.category);
                }
              });
            }
            
            return brandPromotions;
          } catch (error) {
            console.error(`Error fetching promotions for brand ${brand.id}:`, error);
            return [];
          }
        });
        
        const batchResults = await Promise.all(batchPromises);
        additionalPromotions.push(...batchResults.flat());
        
        // Small delay between batches to not overwhelm Firebase
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      // Sort all promotions
      additionalPromotions.sort((a, b) => {
        if (a.createdAt && b.createdAt) {
          return b.createdAt.toDate() - a.createdAt.toDate();
        }
        return 0;
      });
      
      // Update cache with complete data
      this.setCachedPromotions({
        promotions: additionalPromotions,
        categories: this.sortCategories(Array.from(categorySet))
      });
      
      console.log('Background fetch completed');
      
    } catch (error) {
      console.error('Background fetch failed:', error);
    }
  }

  /**
   * Fetch all promotions (fallback method)
   */
  async fetchAllPromotions(forceRefresh = false) {
    return this.fetchInitialPromotions(1000, forceRefresh); // Large limit to get all
  }

  /**
   * Fetch promotions for a specific brand
   */
  async fetchPromotionsByBrand(brandId) {
    try {
      const promotionsRef = collection(doc(db, "brands", brandId), "promotions");
      const promotionsSnapshot = await getDocs(promotionsRef);
      
      const promotions = [];
      
      if (!promotionsSnapshot.empty) {
        promotionsSnapshot.forEach(promoDoc => {
          const promoData = promoDoc.data();
          promotions.push({
            ...promoData,
            id: promoDoc.id,
            brandId: brandId
          });
        });
      }
      
      return {
        success: true,
        data: promotions
      };
      
    } catch (error) {
      console.error(`Error fetching promotions for brand ${brandId}:`, error);
      return {
        success: false,
        error: error.message || "Failed to load brand promotions",
        data: []
      };
    }
  }

  /**
   * Filter promotions based on category and search query
   */
  filterPromotions(promotions, filters = {}) {
    const { category = 'All', searchQuery = '', brandId = null } = filters;

    return promotions.filter(promo => {
      // Category filter with combined category handling
      let matchesCategory = category === 'All';

      if (!matchesCategory && promo.category) {
        const promoCategory = promo.category.toLowerCase();
        const filterCategory = category.toLowerCase();

        // Handle combined categories
        if (filterCategory === 'losing rescue') {
          matchesCategory = promoCategory === 'losing' || promoCategory === 'rescue';
        } else if (filterCategory === 'rebate') {
          matchesCategory = promoCategory.includes('rebate');
        } else {
          matchesCategory = promoCategory === filterCategory;
        }
      }

      // Search query filter
      const matchesSearch = !searchQuery ||
        (promo.title && promo.title.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (promo.description && promo.description.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (promo.brandName && promo.brandName.toLowerCase().includes(searchQuery.toLowerCase()));

      // Brand filter (optional)
      const matchesBrand = !brandId || promo.brandId === brandId;

      return matchesCategory && matchesSearch && matchesBrand;
    });
  }

  /**
   * Search promotions by query
   */
  searchPromotions(promotions, query) {
    if (!query || query.trim() === '') {
      return promotions;
    }
    
    const searchTerm = query.toLowerCase().trim();
    
    return promotions.filter(promo => {
      return (
        (promo.title && promo.title.toLowerCase().includes(searchTerm)) ||
        (promo.description && promo.description.toLowerCase().includes(searchTerm)) ||
        (promo.brandName && promo.brandName.toLowerCase().includes(searchTerm)) ||
        (promo.category && promo.category.toLowerCase().includes(searchTerm))
      );
    });
  }

  /**
   * Get promotion categories from cached data
   */
  getCategories() {
    const cachedData = this.getCachedPromotions();
    if (cachedData && cachedData.categories) {
      return this.sortCategories(cachedData.categories);
    }
    return ['All'];
  }

  /**
   * Get promotion count for a specific brand
   */
  async getPromotionCountForBrand(brandId) {
    try {
      const promotionsRef = collection(doc(db, "brands", brandId), "promotions");
      const promotionsSnapshot = await getDocs(promotionsRef);
      
      return {
        success: true,
        count: promotionsSnapshot.size
      };
      
    } catch (error) {
      console.error(`Error fetching promotion count for brand ${brandId}:`, error);
      return {
        success: false,
        count: 0,
        error: error.message
      };
    }
  }

  /**
   * Get promotion counts for multiple brands
   */
  async getPromotionCountsForBrands(brandIds) {
    try {
      const countPromises = brandIds.map(async (brandId) => {
        const result = await this.getPromotionCountForBrand(brandId);
        return {
          brandId,
          count: result.count,
          success: result.success
        };
      });
      
      const results = await Promise.all(countPromises);
      
      // Convert to object format
      const counts = {};
      results.forEach(result => {
        counts[result.brandId] = result.count;
      });
      
      return {
        success: true,
        data: counts
      };
      
    } catch (error) {
      console.error("Error fetching promotion counts:", error);
      return {
        success: false,
        data: {},
        error: error.message
      };
    }
  }
}

// Create and export a singleton instance
const promotionService = new PromotionService();
export default promotionService;